import { Link } from 'react-router'
import { AlertTriangle, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { Button, ChangePill, SectionHeader, Skeleton } from '@/components/ui/primitives'
import { useFormat } from '@/lib/format'
import { useWatchlist } from '@/store/watchlist'
import { AssetIcon, Sparkline } from './AssetVisuals'
import { WatchButton } from './WatchButton'
import { useMarketQuotes, type MarketQuote, type QuotesState } from './useMarkets'

/** Localized asset name; crypto outside our translations falls back to the provider's name. */
export function AssetName({ id, name }: { id: string; name?: string }) {
  const { t } = useTranslation()
  return <>{t(`assets.${id}` as 'assets.XAU', { defaultValue: name ?? id })}</>
}

/** Text explaining what a change percentage is measured against. */
export function useChangeBasis() {
  const { t } = useTranslation()
  const { date } = useFormat()
  return (change: NonNullable<MarketQuote['change']>) =>
    change.basis === '24h' ? t('markets.last24h') : t('markets.since', { date: date(`${change.since}T00:00:00Z`) })
}

/**
 * Links to every data source shown in a list. Required by Live Coin Watch on each screen
 * using its data, and useful transparency for the others.
 */
export function SourceCredits({ quotes, className }: { quotes: MarketQuote[]; className?: string }) {
  const { t } = useTranslation()
  const sources = [...new Map(quotes.filter((q) => q.source).map((q) => [q.source!.name, q.source!])).values()]
  if (sources.length === 0) return null
  return (
    <p className={clsx('text-[0.7rem] leading-relaxed text-subtle', className)}>
      {t('markets.sources')}{' '}
      {sources.map((s, i) => (
        <span key={s.name}>
          {i > 0 && ' · '}
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:text-fg hover:underline">
            {s.name}
          </a>
        </span>
      ))}
    </p>
  )
}

const trendOf = (q: MarketQuote) => ((q.change?.pct ?? 0) >= 0 ? 'up' : 'down')

/** Small status label used where a price would be. */
export function QuoteStatus({ quote }: { quote: MarketQuote }) {
  const { t } = useTranslation()
  if (quote.status === 'coming_soon') {
    return (
      <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[0.7rem] font-semibold text-muted ring-1 ring-line">
        {t('common.comingSoon')}
      </span>
    )
  }
  return <span className="text-xs text-down">{t('markets.unavailable')}</span>
}

/** Change pill with its basis ("since the close of …") as an accessible hint. */
export function QuoteChange({ quote }: { quote: MarketQuote }) {
  const basisOf = useChangeBasis()
  if (!quote.change) return null
  const hint = basisOf(quote.change)
  return (
    <span title={hint}>
      <ChangePill value={quote.change.pct} />
      <span className="sr-only"> {hint}</span>
    </span>
  )
}

export function MarketsError({ state }: { state: QuotesState }) {
  const { t } = useTranslation()
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl bg-down-soft px-3 py-2.5 text-xs text-down">
      <AlertTriangle className="size-4 shrink-0" aria-hidden />
      <span className="flex-1">{t('markets.loadError')}</span>
      <Button size="sm" variant="outline" onClick={state.refetch} disabled={state.isFetching}>
        <RefreshCw className={clsx('size-3.5', state.isFetching && 'animate-spin')} aria-hidden />
        {t('converter.retry')}
      </Button>
    </div>
  )
}

function TickerCard({ quote }: { quote: MarketQuote }) {
  const { fixed } = useFormat()
  return (
    <Link
      to={`/markets/${quote.id}`}
      className="card flex min-w-[13.5rem] shrink-0 snap-start items-center gap-3 p-3 transition hover:border-line-strong md:min-w-0"
    >
      <AssetIcon asset={quote} className="size-10" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="ltr truncate text-sm font-semibold">{quote.symbol}</span>
          <QuoteChange quote={quote} />
        </div>
        <div className="mt-1 flex items-end justify-between gap-2">
          <span className="num ltr text-[0.95rem] font-semibold">{fixed(quote.price!.toFixed(quote.decimals), quote.decimals)}</span>
          {quote.spark && quote.spark.length > 1 && (
            <Sparkline data={quote.spark} trend={trendOf(quote)} className="h-7 w-16" />
          )}
        </div>
      </div>
    </Link>
  )
}

export function MarketStrip() {
  const { t, i18n } = useTranslation()
  const state = useMarketQuotes()
  const Chevron = i18n.dir() === 'rtl' ? ChevronLeft : ChevronRight
  const watched = useWatchlist((s) => s.ids)
  const available = (state.quotes ?? []).filter((q) => q.status === 'ok')
  const rank = (id: string) => (watched.includes(id) ? watched.indexOf(id) : watched.length + 1)
  const items = [...available].sort((a, b) => rank(a.id) - rank(b.id)).slice(0, 6)
  return (
    <section aria-labelledby="snapshot-title">
      <SectionHeader
        className="mb-3"
        title={<span id="snapshot-title">{t('home.snapshot')}</span>}
        action={
          <Link to="/markets" className="inline-flex items-center gap-0.5 text-sm font-medium text-gold-text hover:underline">
            {t('common.viewAll')}
            <Chevron className="size-4" />
          </Link>
        }
      />
      {state.status === 'error' ? (
        <MarketsError state={state} />
      ) : (
        <div className="scrollbar-none -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 xl:grid-cols-3 2xl:grid-cols-6">
          {state.status === 'loading'
            ? Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-[74px] min-w-[13.5rem] rounded-[1.25rem] md:min-w-0" />)
            : items.map((q) => <TickerCard key={q.id} quote={q} />)}
        </div>
      )}
      <SourceCredits quotes={items} className="mt-2" />
    </section>
  )
}

export function AssetRow({ quote, dense = false, watch = !dense }: { quote: MarketQuote; dense?: boolean; watch?: boolean }) {
  const { t } = useTranslation()
  const { fixed } = useFormat()
  const ok = quote.status === 'ok'
  return (
    <li className="flex items-center gap-1 rounded-xl transition hover:bg-surface-2">
      <Link
        to={`/markets/${quote.id}`}
        className={clsx('flex min-w-0 flex-1 items-center gap-3 rounded-xl px-2', dense ? 'py-2' : 'py-2.5')}
      >
        <AssetIcon asset={quote} className={clsx(dense ? 'size-9' : 'size-10', !ok && 'opacity-60')} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            <AssetName id={quote.id} name={quote.name} />
          </p>
          <p className="text-xs text-subtle">
            <span className="ltr">{quote.symbol}</span>
            {quote.unit && !dense && <span className="ms-1 hidden sm:inline">· {t(`assets.${quote.unit}`)}</span>}
          </p>
        </div>
        {ok && quote.spark && quote.spark.length > 1 && (
          <Sparkline data={quote.spark} trend={trendOf(quote)} className={clsx('h-8 w-20', dense && 'hidden xs:block')} />
        )}
        <div className="flex min-w-[5.5rem] flex-col items-end gap-0.5">
          {ok ? (
            <>
              <span className="num ltr text-sm font-semibold">{fixed(quote.price!.toFixed(quote.decimals), quote.decimals)}</span>
              <QuoteChange quote={quote} />
            </>
          ) : (
            <QuoteStatus quote={quote} />
          )}
        </div>
      </Link>
      {watch && <WatchButton id={quote.id} className="me-1" />}
    </li>
  )
}

export function MoversCard({ className }: { className?: string }) {
  const { t } = useTranslation()
  const state = useMarketQuotes()
  const movers = (state.quotes ?? [])
    .filter((q) => q.status === 'ok' && q.change)
    .sort((a, b) => Math.abs(b.change!.pct) - Math.abs(a.change!.pct))
    .slice(0, 6)
  return (
    <section className={clsx('card p-4 sm:p-5', className)}>
      <SectionHeader className="mb-2" title={t('home.movers')} />
      {state.status === 'error' && <MarketsError state={state} />}
      {state.status === 'loading' && (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      )}
      <ul className="-mx-2">
        {movers.map((q) => (
          <AssetRow key={q.id} quote={q} dense />
        ))}
      </ul>
      <p className="mt-2 text-[0.7rem] text-subtle">{t('markets.moversNote')}</p>
      <SourceCredits quotes={movers} className="mt-1" />
    </section>
  )
}
